import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Polygon} from 'geojson';
import {ComponentType} from 'react';
import {connect} from 'react-redux';

import {
  TLightProjects,
  TMapProjects,
  TBestFeasibilities,
  TFullProjects,
  TFullProject,
} from 'App/features/searchEntities/lightProjects/lightProjectsTypes';
import {RootState} from 'App/store/rootReducer';

export type LightProjectsSlice = {
  lightProjects: TLightProjects;
  mapProjects: TMapProjects;
  bestFeasibilities: TBestFeasibilities;
  fullProjects: TFullProjects;
  isLightProjectsRequesting: boolean;
  isLightProjectsLoaded: boolean;
  error: string | null;
};

const initialState: LightProjectsSlice = {
  lightProjects: [],
  mapProjects: [],
  bestFeasibilities: [],
  fullProjects: {},
  isLightProjectsRequesting: false,
  isLightProjectsLoaded: false,
  error: null,
};

const lightProjectsSlice = createSlice({
  name: 'lightProjects',
  initialState,
  reducers: {
    addProject: (
      state,
      {
        payload,
      }: PayloadAction<{
        fullProject: TFullProject;
        geometry: Polygon;
      }>,
    ) => {
      state.lightProjects = [
        ...state.lightProjects,
        {hash: payload.fullProject.hash, status: payload.fullProject.status},
      ];
      state.mapProjects = [
        ...state.mapProjects,
        {
          hash: payload.fullProject.hash,
          status: payload.fullProject.status,
          geometry: payload.geometry,
        },
      ];
      state.fullProjects = {
        ...state.fullProjects,
        [payload.fullProject.hash]: payload.fullProject,
      };
    },
    loadLightProjects: (
      state,
      {payload}: PayloadAction<{lightProjects: TLightProjects}>,
    ) => {
      state.lightProjects = payload.lightProjects;
      state.isLightProjectsRequesting = false;
      state.isLightProjectsLoaded = true;
    },
    loadMapProjects: (
      state,
      {payload}: PayloadAction<{mapProjects: TMapProjects}>,
    ) => {
      state.mapProjects = payload.mapProjects;
    },
    loadBestFeasibilities: (
      state,
      {payload}: PayloadAction<{bestFeasibilities: TBestFeasibilities}>,
    ) => {
      state.bestFeasibilities = payload.bestFeasibilities;
    },
    resetLightProjects: state => {
      state.lightProjects = [];
      state.mapProjects = [];
      state.bestFeasibilities = [];
    },
    loadFullProjects: (
      state,
      {payload}: PayloadAction<{fullProjects: TFullProjects}>,
    ) => {
      state.fullProjects = {
        ...state.fullProjects,
        ...payload.fullProjects,
      };
    },
    resetFullProject: (state, {payload}: PayloadAction<string>) => {
      delete state.fullProjects[payload];
    },
    updateProject: (
      state,
      {payload}: PayloadAction<{updatedLightProject: TFullProject}>,
    ) => {
      const hash = payload.updatedLightProject.hash;
      state.fullProjects[hash] = payload.updatedLightProject;
      state.lightProjects = state.lightProjects.map(project => {
        if (project.hash === payload.updatedLightProject.hash) {
          return {
            ...project,
            status: payload.updatedLightProject.status,
          };
        }
        return project;
      });
      state.mapProjects = state.mapProjects.map(project => {
        if (project.hash === payload.updatedLightProject.hash) {
          return {
            ...project,
            status: payload.updatedLightProject.status,
          };
        }
        return project;
      });
    },
    deleteProject: (state, {payload}: PayloadAction<{projectHash: string}>) => {
      state.lightProjects = state.lightProjects.filter(
        p => p.hash !== payload.projectHash,
      );
      state.mapProjects = state.mapProjects.filter(
        p => p.hash !== payload.projectHash,
      );
      delete state.fullProjects[payload.projectHash];
    },
    requesting: state => {
      state.isLightProjectsRequesting = true;
      state.isLightProjectsLoaded = false;
    },
    success: state => {
      state.isLightProjectsRequesting = false;
      state.isLightProjectsLoaded = true;
    },
    fail: (state, {payload}: PayloadAction<{error: string}>) => {
      state.isLightProjectsRequesting = false;
      state.isLightProjectsLoaded = false;
      state.error = payload.error;
    },
  },
});

const mapLightProjectsToProps = (state: RootState): LightProjectsSlice =>
  state.searchEntities.lightProjects;
export const connectToLightProjects = (
  Component: ComponentType<LightProjectsSlice>,
): ComponentType =>
  connect(mapLightProjectsToProps, null, null, {forwardRef: true})(Component);

export const selectLightProjects = (state: RootState): TLightProjects =>
  state.searchEntities.lightProjects.lightProjects;

export const selectMapProjects = (state: RootState): TMapProjects =>
  state.searchEntities.lightProjects.mapProjects;

export const selectBestFeasibilities = (state: RootState): TBestFeasibilities =>
  state.searchEntities.lightProjects.bestFeasibilities;

export const selectFullProjects = (state: RootState): TFullProjects =>
  state.searchEntities.lightProjects.fullProjects;

export const selectFullProjectByHash =
  (hash: string) =>
  (state: RootState): TFullProject | null =>
    state.searchEntities.lightProjects.fullProjects[hash] || null;

export const selectLightProjectsRequesting = (state: RootState): boolean =>
  state.searchEntities.lightProjects.isLightProjectsRequesting;

export const {
  addProject,
  loadLightProjects,
  loadMapProjects,
  loadBestFeasibilities,
  resetLightProjects,
  loadFullProjects,
  resetFullProject,
  updateProject,
  deleteProject,
  requesting,
  success,
  fail,
} = lightProjectsSlice.actions;

export default lightProjectsSlice.reducer;
